import { ReactNode } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import Introduction from '@components/common/Introduction';

/**
 * Shared layout for authentication-related pages.
 *
 * Used by:
 * - Login
 * - Forgot Password
 *
 * Responsibilities:
 * - Responsive split layout (branding panel + form)
 * - Consistent visual framing for auth pages
 * - No authentication or business logic
 */
type AuthLayoutProps = {
  /** Page title (e.g. "Sign in") */
  title: string;

  /** Optional subtitle under the title */
  subtitle?: string;

  /** Main content (forms, alerts, etc.) */
  children: ReactNode;

  /** Optional footer area (e.g. legal or security notes) */
  footer?: ReactNode;

  /**
   * Branding text for mobile header.
   * Defaults are provided for general use,
   * but can be overridden if needed in the future.
   */
  brandTitle?: string;
  brandSubtitle?: string;

  /**
   * Mobile-only one-liner to replace the left Introduction panel.
   * Keep it short (1–2 lines max).
   */
  brandTagline?: string;
};

const DEFAULT_BRAND_TITLE = 'eSIM Station';
const DEFAULT_BRAND_SUBTITLE = 'Admin & Partner Console';
const DEFAULT_BRAND_TAGLINE =
  'Wholesale eSIM distribution across global and local operators.';

const AuthLayout = ({
  title,
  subtitle,
  children,
  footer,
  brandTitle = DEFAULT_BRAND_TITLE,
  brandSubtitle = DEFAULT_BRAND_SUBTITLE,
  brandTagline = DEFAULT_BRAND_TAGLINE,
}: AuthLayoutProps) => {
  const theme = useTheme();

  // Render left branding panel only on md and larger screens
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      sx={{
        minHeight: '100vh',

        /**
         * Page background
         * - Subtle gradients for depth
         * - Different palettes for light / dark mode
         * - Keeps focus on the center form card
         */
        background: (t) => {
          const isDark = t.palette.mode === 'dark';

          return isDark
            ? `radial-gradient(
                 1200px 800px at 20% 10%,
                 rgba(144,202,249,0.10) 0%,
                 rgba(0,0,0,0) 55%
               ),
               radial-gradient(
                 900px 700px at 90% 30%,
                 rgba(156,39,176,0.08) 0%,
                 rgba(0,0,0,0) 55%
               ),
               linear-gradient(
                 135deg,
                 ${t.palette.grey[900]} 0%,
                 #0b0f1a 100%
               )`
            : `radial-gradient(
                 900px 700px at 15% 10%,
                 rgba(25,118,210,0.08) 0%,
                 rgba(255,255,255,0) 55%
               ),
               radial-gradient(
                 900px 700px at 85% 20%,
                 rgba(156,39,176,0.06) 0%,
                 rgba(255,255,255,0) 55%
               ),
               linear-gradient(
                 135deg,
                 ${t.palette.grey[50]} 0%,
                 ${t.palette.grey[100]} 60%,
                 ${t.palette.grey[200]} 100%
               )`;
        },
      }}
    >
      <Grid container minHeight="100vh">
        {/* Left panel: branding / introduction (desktop only) */}
        {mdUp && (
          <Grid item md={6}>
            <Introduction />
          </Grid>
        )}

        {/* Right panel: authentication form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'grid',
            placeItems: 'center',
            px: 2,

            // Slightly tighter vertical padding on mobile to reduce "floating" feeling
            py: { xs: 3, md: 0 },
          }}
        >
          <Container maxWidth="sm">
            {/* Mobile branding header (shown only below md) */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <Stack spacing={0.75}>
                {/* Brand row: Title + badge-style subtitle */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography
                    variant="h6"
                    fontWeight={900}
                    sx={{ letterSpacing: -0.3 }}
                  >
                    {brandTitle}
                  </Typography>

                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      border: (t) => `1px solid ${t.palette.divider}`,
                      bgcolor: (t) =>
                        t.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(0,0,0,0.04)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontWeight: 600 }}
                    >
                      {brandSubtitle}
                    </Typography>
                  </Box>
                </Stack>

                {/* Mobile tagline: compact value statement */}
                {brandTagline && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.45,
                      maxWidth: 520,
                    }}
                  >
                    {brandTagline}
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Auth card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                boxShadow: (t) =>
                  t.palette.mode === 'dark'
                    ? '0 14px 50px rgba(0,0,0,0.55)'
                    : '0 12px 40px rgba(0,0,0,0.08)',
              }}
            >
              {/* Title & subtitle */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={800}>
                  {title}
                </Typography>

                {subtitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>

              {/* Main content */}
              {children}

              {/* Optional footer */}
              {footer && <Box sx={{ mt: 2 }}>{footer}</Box>}
            </Paper>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AuthLayout;
